/**
 * ReconciliationPanel — riconciliazione semi-automatica fatture TeamSystem ↔ ordini (solo admin).
 * - Suggerimenti: fatture non collegate con candidati (stesso cliente, importo entro ±1€)
 * - Non abbinate: collegamento manuale (anche multi-ordine per fatture raggruppate)
 * - Collegate: elenco con possibilità di scollegare
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Link2Off, Zap, RefreshCw, CheckCircle2, AlertTriangle, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { API } from "../utils/format";
import { formatMoney } from "../utils/currency";

const fmtDate = (iso) => (iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("it-IT") : "—");

const ReconciliationPanel = ({ company, reloadKey, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [manualFor, setManualFor] = useState(null); // fattura per collegamento manuale
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reconciliation/suggestions`);
      setData(r.data);
    } catch (e) {
      toast.error("Errore caricamento riconciliazione");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const runAuto = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/reconciliation/auto`);
      toast.success(`Riconciliazione automatica: ${r.data.auto_linked} fatture collegate, ${r.data.orders_settled} ordini saldati`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore riconciliazione automatica");
    } finally {
      setRunning(false);
    }
  };

  const link = async (inv, orderIds) => {
    try {
      const r = await axios.post(`${API}/admin/reconciliation/link`, {
        customer_id: inv.customer_id,
        doc_number: inv.doc_number,
        order_ids: orderIds,
      });
      toast.success(r.data.settled > 0
        ? `Fattura ${inv.doc_number} collegata — ${r.data.settled} ordine/i SALDATI (fattura pagata)`
        : `Fattura ${inv.doc_number} collegata a ${r.data.linked} ordine/i`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore collegamento");
    }
  };

  const unlink = async (inv) => {
    try {
      await axios.post(`${API}/admin/reconciliation/unlink`, { customer_id: inv.customer_id, doc_number: inv.doc_number });
      toast.success(`Fattura ${inv.doc_number} scollegata`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const filterFn = (e) =>
    !search ||
    (e.business_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (e.doc_number || "").toLowerCase().includes(search.toLowerCase());

  if (loading && !data) return <div className="p-10 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>;
  const suggestions = (data?.suggestions || []).filter(filterFn);
  const unmatched = (data?.unmatched || []).filter(filterFn);
  const linked = (data?.linked || []).filter(filterFn);

  return (
    <div className="space-y-4" data-testid="reconciliation-panel">
      <Card className="p-4 bg-gradient-to-r from-[#8A1C2A]/5 to-[#C5A059]/10 border-[#C5A059]/40">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-stone-900 flex items-center gap-2"><Link2 className="w-4 h-4 text-[#8A1C2A]" /> Riconciliazione fatture ↔ ordini</p>
            <p className="text-xs text-stone-600 mt-0.5">
              Collega le fatture fiscali importate agli ordini spediti. Quando una fattura risulta pagata (residuo 0), l'ordine collegato viene <b>saldato e completato</b> automaticamente.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" size="sm" className="border-stone-300" data-testid="reconciliation-reload-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Aggiorna
            </Button>
            <Button onClick={runAuto} disabled={running} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" size="sm" data-testid="reconciliation-auto-btn">
              <Zap className="w-3.5 h-3.5 mr-1" /> {running ? "In corso..." : "Riconciliazione automatica"}
            </Button>
          </div>
        </div>
      </Card>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca cliente o n° fattura..."
          className="w-full pl-10 pr-3 py-2 text-sm bg-white border border-stone-300 rounded-md"
          data-testid="reconciliation-search"
        />
      </div>

      {/* SUGGERIMENTI */}
      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 bg-amber-50">
          <p className="text-sm font-semibold text-stone-900">💡 Suggerimenti ({suggestions.length})</p>
          <p className="text-xs text-stone-500">Fatture con ordini candidati (stesso cliente, importo entro ±1 €). Verifica e collega.</p>
        </div>
        {suggestions.length === 0 ? (
          <p className="p-5 text-sm text-stone-500 italic">Nessun suggerimento: nessuna fattura non collegata ha ordini con importo compatibile.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {suggestions.map((inv) => (
              <div key={`${inv.customer_id}-${inv.doc_number}`} className="p-3 flex flex-wrap items-center gap-3" data-testid={`suggestion-${inv.doc_number}`}>
                <div className="min-w-[220px]">
                  <p className="text-sm font-semibold text-stone-900">Fatt. {inv.doc_number} · {fmtDate(inv.doc_date)}</p>
                  <p className="text-xs text-stone-600">{inv.business_name}</p>
                  <p className="text-xs mt-0.5">
                    {formatMoney(inv.imp_documento, company)}{" "}
                    {inv.is_paid
                      ? <Badge className="bg-green-100 text-green-700 text-[10px] ml-1">PAGATA</Badge>
                      : <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-1">aperta {formatMoney(inv.residuo, company)}</Badge>}
                  </p>
                </div>
                <div className="flex-1 space-y-1.5">
                  {inv.candidates.map((c) => (
                    <div key={c.order_id} className="flex flex-wrap items-center gap-2 bg-stone-50 border border-stone-200 rounded-md px-3 py-1.5">
                      <span className="font-mono text-xs text-[#8A1C2A] font-semibold">{c.order_number}</span>
                      <span className="text-xs text-stone-600">{formatMoney(c.grand_total, company)}</span>
                      <span className="text-[10px] text-stone-500 font-semibold">DDT {c.document_reference || "—"}</span>
                      {c.shipping_date && <span className="text-[10px] text-stone-400">{fmtDate(c.shipping_date)}</span>}
                      {c.match === "exact" ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px]"><CheckCircle2 className="w-3 h-3 inline mr-0.5" />importo esatto</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px]"><AlertTriangle className="w-3 h-3 inline mr-0.5" />diff. {c.diff > 0 ? "+" : ""}{c.diff.toFixed(2)} €</Badge>
                      )}
                      <Button size="sm" onClick={() => link(inv, [c.order_id])} className="ml-auto h-7 bg-[#8A1C2A] hover:bg-[#6B1420] text-white text-xs" data-testid={`link-${inv.doc_number}-${c.order_number}`}>
                        <Link2 className="w-3 h-3 mr-1" /> {inv.is_paid ? "Collega e salda" : "Collega"}
                      </Button>
                    </div>
                  ))}
                  {inv.candidates.length > 1 && (
                    <p className="text-[10px] text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Più DDT con importo compatibile: verifica il numero DDT e scegli quello giusto.</p>
                  )}
                  <button
                    onClick={() => setManualFor(inv)}
                    className="text-[11px] text-stone-500 underline hover:text-[#8A1C2A]"
                    data-testid={`manual-from-suggestion-${inv.doc_number}`}
                  >
                    Scegli un altro ordine/DDT…
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* NON ABBINATE (manuale) */}
      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 bg-stone-50">
          <p className="text-sm font-semibold text-stone-900">Fatture senza abbinamento ({unmatched.length})</p>
          <p className="text-xs text-stone-500">Nessun ordine con importo simile (o fattura raggruppata su più DDT): collega manualmente uno o più ordini.</p>
        </div>
        {unmatched.length === 0 ? (
          <p className="p-5 text-sm text-stone-500 italic">Tutte le fatture hanno un suggerimento o sono già collegate.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-stone-100">
            {unmatched.map((inv) => (
              <div key={`${inv.customer_id}-${inv.doc_number}`} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`unmatched-${inv.doc_number}`}>
                <span className="font-mono font-semibold text-stone-900 w-24">{inv.doc_number}</span>
                <span className="text-xs text-stone-500 w-20">{fmtDate(inv.doc_date)}</span>
                <span className="flex-1 truncate text-stone-700">{inv.business_name}</span>
                <span className="font-semibold">{formatMoney(inv.imp_documento, company)}</span>
                {inv.is_paid
                  ? <Badge className="bg-green-100 text-green-700 text-[10px]">PAGATA</Badge>
                  : <Badge className="bg-blue-100 text-blue-700 text-[10px]">aperta</Badge>}
                <Button size="sm" variant="outline" onClick={() => setManualFor(inv)} className="h-7 border-stone-300 text-xs" data-testid={`manual-link-${inv.doc_number}`}>
                  <Link2 className="w-3 h-3 mr-1" /> Collega manualmente
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* COLLEGATE */}
      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 bg-green-50">
          <p className="text-sm font-semibold text-stone-900">✓ Fatture collegate ({linked.length})</p>
        </div>
        {linked.length === 0 ? (
          <p className="p-5 text-sm text-stone-500 italic">Nessuna fattura collegata a ordini.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-stone-100">
            {linked.map((inv) => (
              <div key={`${inv.customer_id}-${inv.doc_number}`} className="px-3 py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`linked-${inv.doc_number}`}>
                <span className="font-mono font-semibold text-stone-900 w-24">{inv.doc_number}</span>
                <span className="flex-1 truncate text-stone-700">{inv.business_name}</span>
                <span>{formatMoney(inv.imp_documento, company)}</span>
                {inv.is_paid
                  ? <Badge className="bg-green-100 text-green-700 text-[10px]">PAGATA</Badge>
                  : <Badge className="bg-blue-100 text-blue-700 text-[10px]">aperta</Badge>}
                <span className="text-xs text-stone-500">
                  → {(inv.linked_orders || []).map((o) => `${o.order_number}${o.document_reference ? ` (DDT ${o.document_reference})` : ""}${o.is_fully_paid ? " ✓saldato" : ""}`).join(", ") || `${inv.linked_order_ids.length} ordini`}
                </span>
                <Button size="sm" variant="ghost" onClick={() => unlink(inv)} className="h-7 text-red-600 hover:bg-red-50 text-xs" data-testid={`unlink-${inv.doc_number}`}>
                  <Link2Off className="w-3 h-3 mr-1" /> Scollega
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ManualLinkDialog invoice={manualFor} company={company} onClose={() => setManualFor(null)} onLink={(ids) => { link(manualFor, ids); setManualFor(null); }} />
    </div>
  );
};

const ManualLinkDialog = ({ invoice, company, onClose, onLink }) => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    setSelected(new Set());
    setLoading(true);
    axios.get(`${API}/admin/reconciliation/orders`, { params: { customer_id: invoice.customer_id } })
      .then((r) => setOrders(r.data || []))
      .catch(() => toast.error("Errore caricamento ordini"))
      .finally(() => setLoading(false));
  }, [invoice]);

  if (!invoice) return null;
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectedTotal = orders.filter((o) => selected.has(o.id)).reduce((s, o) => s + (o.grand_total_resolved || 0), 0);
  const diff = selectedTotal - (invoice.imp_documento || 0);

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="manual-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-[#8A1C2A]" /> Collega fattura {invoice.doc_number}</DialogTitle>
          <p className="text-xs text-stone-500">{invoice.business_name} · {formatMoney(invoice.imp_documento, company)} · seleziona uno o più ordini (fatture raggruppate = più DDT)</p>
        </DialogHeader>
        {loading ? (
          <div className="p-8 text-center"><div className="spinner w-5 h-5 mx-auto" /></div>
        ) : orders.length === 0 ? (
          <p className="p-6 text-sm text-stone-500 italic text-center">Nessun ordine spedito non collegato per questo cliente.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-stone-200 rounded-md divide-y divide-stone-100">
            {orders.map((o) => (
              <label key={o.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-stone-50" data-testid={`manual-order-${o.order_number}`}>
                <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4 accent-[#8A1C2A]" />
                <span className="font-mono text-xs font-semibold text-[#8A1C2A]">{o.order_number}</span>
                <span className="text-xs text-stone-500">{fmtDate((o.shipping_date || o.created_at || "").slice(0, 10))}</span>
                {o.document_reference && <span className="text-[10px] text-stone-400">DDT {o.document_reference}</span>}
                <span className="ml-auto font-semibold">{formatMoney(o.grand_total_resolved, company)}</span>
              </label>
            ))}
          </div>
        )}
        {selected.size > 0 && (
          <div className={`text-sm px-3 py-2 rounded-md ${Math.abs(diff) <= 0.01 ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`} data-testid="manual-link-diff">
            Selezionati: <b>{formatMoney(selectedTotal, company)}</b> · Fattura: <b>{formatMoney(invoice.imp_documento, company)}</b> · Differenza: <b>{diff > 0 ? "+" : ""}{diff.toFixed(2)} €</b>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={() => onLink(Array.from(selected))} disabled={selected.size === 0} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="manual-link-confirm">
            <Link2 className="w-4 h-4 mr-1" /> Collega {selected.size} ordine/i {invoice.is_paid ? "e salda" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReconciliationPanel;
